import React, {
  ComponentProps,
  ComponentType,
  useContext,
  useEffect,
  useState,
} from 'react'

import { registerPrelaunchJob } from '@emico/prelaunch-jobs'
import { globalWindow } from '@emico/ssr-utils'
import { retryingPromise } from '@emico/utils'

import LazyFallbackContext from './LazyFallbackContext'

declare global {
  interface Window {
    /** Which chunks have actually been loaded during this session. */
    activeChunks?: string[]
    /** Which chunks should be preloaded before start the app for a loader-free render. */
    preloadChunks?: string[]
  }
}

/**
 * Register that this chunk is active on this page so that it can be eager
 * loaded next time.
 */
const registerActiveChunk = (id: string) => {
  // TODO: SSR Fixes required
  if (!globalWindow) {
    return
  }
  globalWindow.activeChunks = globalWindow.activeChunks || []
  if (!globalWindow.activeChunks.includes(id)) {
    globalWindow.activeChunks.push(id)
  }
}

/**
 * This is an extended imitation of React.lazy. React.lazy makes a lazy
 * component that is only loaded once it is being rendered.
 * See https://reactjs.org/docs/code-splitting.html#reactlazy
 *
 * This adds eager loading in the sense that if a chunk is known ahead of time
 * to be necessary, it will load that before it is being rendered. If it can be
 * loaded before being rendered, it will skip the Promise and render
 * immediately. This allows us to prevent showing loading indicators.
 *
 * This is necessary for optimal rendering when using prerendering (or SSR).
 * Prerendering will add all chunks to the head, so that they'll be loaded
 * automatically. However, because Promises are guaranteed to be resolved
 * asynchronously, using the standard React.lazy will always show a loading
 * indicator for at least 1 frame, even if the chunk was already loaded. This
 * solution solves this by executing the Promise early and after loading is
 * finished, skipping it entirely.
 *
 * This will automatically retry chunk loading 3 times.
 *
 * @param id A unique identifier for the chunk. Should match the `webpackChunkName`, if set.
 * @param chunkFactory The chunk import method.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazy = <T extends ComponentType<any>>(
  id: string,
  chunkFactory: () => Promise<{ default: T }>,
) => {
  // This code is executed at definition; before the component is actually being
  // rendered.
  // This function acts as a closure. The below vars, result and promise, are
  // its internal state that persist for the entire runtime of the app.
  if (process.env.REACT_APP_DEBUG_PRERENDERING) {
    console.log('lazy', 'Registered', id)
  }

  let result: T | undefined
  let promise: Promise<T> | undefined

  const loadChunk = () => {
    if (!promise) {
      promise = (async () => {
        registerActiveChunk(id)

        const promise = retryingPromise(chunkFactory).then(
          (module) => module.default,
        )
        registerPrelaunchJob(id, promise)
        result = await promise
        if (process.env.REACT_APP_DEBUG_PRERENDERING) {
          console.log('lazy', id, 'ready')
        }
        return result
      })()
    }
    return promise
  }

  if (globalWindow?.preloadChunks?.includes(id)) {
    if (process.env.REACT_APP_DEBUG_PRERENDERING) {
      console.log('lazy', 'Preloading', id)
    }
    loadChunk()
  }

  /* eslint-disable react-hooks/rules-of-hooks */
  const component = (props: ComponentProps<T>) => {
    // This is the part of the code that is executed when the component is
    // being rendered. It must start loading the chunk if it hasn't been
    // loaded yet and isn't currently being loaded.

    // Initial value needs to be a function or otherwise functional components
    // won't load.
    const [Component, setComponent] = useState<T | undefined>(() => result)
    const lazyFallback = useContext(LazyFallbackContext)

    useEffect(() => {
      if (Component) {
        return
      }
      if (!promise) {
        if (process.env.REACT_APP_DEBUG_PRERENDERING) {
          console.log('lazy', 'Lazy loading', id)
        }
        promise = loadChunk()
      }
      promise.then((newComponent) => {
        // Needs to be a function or otherwise functional components won't load.
        setComponent(() => newComponent)
      })
    }, [Component])

    if (!Component) {
      if (process.env.REACT_APP_DEBUG_PRERENDERING) {
        console.log('lazy', 'Waiting for', id, '...')
      }

      return lazyFallback
    }

    return <Component {...props} />
  }
  component.displayName = `lazy(${id})`
  // TODO: Some lazy components may share ids (e.g. catalog: PLP and search). We should preload all components thar use the same chunk based on their id.
  component.preload = loadChunk

  return component
}

export default lazy
